/**
 * Sync Session Component
 *
 * OAuth callback handler that receives tokens from central auth
 * Supports both URL parameters and iframe postMessage
 */

import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Box, CircularProgress, Typography } from '@mui/material';
import { useAuthStore } from '../../store/authStore';
import { centralSyncFrameUrl, getCentralAppUrl } from '../../config/authDomain';

export const SyncSession: React.FC = () => {
  const centralAppUrl = getCentralAppUrl();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { login } = useAuthStore();
  const isAuthenticated = useAuthStore(state => state.isAuthenticated);
  const [status, setStatus] = useState<'processing' | 'success' | 'error'>('processing');
  const [tokenProcessed, setTokenProcessed] = useState(false);

  // Only a same-origin path is honoured — `returnTo` arrives from a redirect we
  // do not control, and an absolute URL here would be an open redirect.
  const requested = searchParams.get('returnTo');
  const returnTo =
    requested && requested.startsWith('/') && !requested.startsWith('//') ? requested : '/';

  useEffect(() => {
    // Method 1: tokens carried back on the URL (primary method). Read before the
    // remembered session below: that flag is persisted and can be stale — an
    // expired token, or a different account — and honouring it first would throw
    // away the credential the user just signed in to get.
    //
    // The fragment is the preferred channel and is checked first: browsers never
    // transmit it, so the credential stays out of server logs, proxies and
    // Referer headers. The query string is still read for callers that predate
    // the fragment handoff.
    const hash = new URLSearchParams(window.location.hash.replace(/^#/, ''));
    const tokenFromUrl = hash.get('token') ?? searchParams.get('token');
    const accessTokenFromUrl = hash.get('accessToken') ?? searchParams.get('accessToken');
    const refreshTokenFromUrl = hash.get('refreshToken') ?? searchParams.get('refreshToken');

    const token = accessTokenFromUrl || tokenFromUrl;

    if (token && !tokenProcessed) {
      // Token found in URL - process authentication
      setTokenProcessed(true);

      // Store tokens and update auth state
      login({
        accessToken: token,
        refreshToken: refreshTokenFromUrl || undefined
      }).then(() => {
        setStatus('success');

        // Security: Clean the URL to remove tokens from browser history. This
        // must also drop the fragment, which replaceState only does when the
        // replacement URL has none of its own.
        window.history.replaceState({}, '', '/sync-session');

        // Back to wherever the sign-in started, so asking for API keys returns
        // you to API keys rather than to the dashboard. Only a same-origin path
        // is honoured — `returnTo` arrives from a redirect we do not control,
        // and an absolute URL here would be an open redirect.
        setTimeout(() => {
          navigate(returnTo, { replace: true });
        }, 500);
      }).catch((error) => {
        console.error('❌ Failed to process token:', error);
        setStatus('error');
      });

      return;
    }

    // Already signed in — the shared cookie was readable, which is the case on a
    // host under the central app's cookie domain. There is nothing to wait for,
    // and waiting would park the user on a spinner for the full timeout before
    // dropping them somewhere they did not ask for.
    if (isAuthenticated && !tokenProcessed) {
      navigate(returnTo, { replace: true });
      return;
    }

    // Method 2: Wait for iframe postMessage (fallback method)
    if (!token && !tokenProcessed) {
      // Waiting for iframe sync...

      // Set timeout for iframe sync
      const timeout = setTimeout(() => {
        if (!tokenProcessed) {
          console.warn('⏱️ Iframe sync timeout - returning to the requested page');
          navigate(returnTo, { replace: true });
        }
      }, 5000);

      return () => clearTimeout(timeout);
    }
  }, [searchParams, navigate, login, tokenProcessed, isAuthenticated, returnTo]);

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        height: '100vh',
        bgcolor: 'background.default',
        flexDirection: 'column',
        gap: 2,
      }}
    >
      {status === 'processing' && (
        <>
          <CircularProgress size={60} />
          <Typography variant="h6" color="text.primary">
            Syncing your session...
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Please wait while we authenticate you
          </Typography>

          {centralAppUrl && (
            <iframe
              src={centralSyncFrameUrl(centralAppUrl)}
              style={{ display: 'none' }}
              title="Auth Sync Frame"
              sandbox="allow-scripts allow-same-origin"
            />
          )}
        </>
      )}

      {status === 'success' && (
        <>
          <Typography variant="h6" color="success.main">
            ✓ Authentication successful!
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Redirecting...
          </Typography>
        </>
      )}

      {status === 'error' && (
        <>
          <Typography variant="h6" color="error.main">
            Authentication failed
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Please try signing in again
          </Typography>
        </>
      )}
    </Box>
  );
};
